/**
 * chickMagnet.ts — Chick Magnet Statue System
 * TheBurntPeanut Simulator 9000
 *
 * 5 marble statues placed in open ground (never inside rooms).
 * Press E near one → every gold + XP orb on the map flies to the player instantly.
 * Statue glows blue and is permanently spent after use (one per statue per run).
 *
 * Depth-sorted rendering: player can walk "behind" the statue.
 * Engine calls renderChickMagnet twice per frame — once for statues behind
 * the player and once for statues in front.
 */

import {
  BORDER_COLLISION_NORTH, BORDER_COLLISION_SOUTH,
  BORDER_COLLISION_WEST,  BORDER_COLLISION_EAST,
} from './borderWalls';

// ─── Types ────────────────────────────────────────────────────

export interface ChickMagnet {
  id: string;
  x: number;
  y: number;
  activated: boolean;
}

// ─── Constants ────────────────────────────────────────────────

export const CHICK_MAGNET_INTERACT_RADIUS = 90;
export const CHICK_MAGNET_H = 220;   // total world-unit height (top of hair to pedestal base)
export const CHICK_MAGNET_W = 130;   // total world-unit width

// The Y depth-sort line: when player.y > this, player is "south of" statue → player in front
export const CHICK_MAGNET_FEET_OFFSET = 30; // magnet.y + this = base of figure (not pedestal)

// ─── Placement ────────────────────────────────────────────────

function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xFFFFFFFF;
  };
}

export function generateChickMagnets(
  seed: number,
  worldSize: number,
  rooms: Array<{ x: number; y: number; size: number }>,
  spawnPoint: { x: number; y: number },
): ChickMagnet[] {
  // BETA 23.28 — DELETED per Dylan ("[the] statue... removed
  // immediately and deleted"). Returns [] forever: PSBOT's autoPlaytester
  // still compiles + iterates an empty list (Cloakzy pattern). The loot-pull
  // idea returns later through the SURGE STATUES.
  return [];
}

// ─── Interaction ──────────────────────────────────────────────

// BETA 14.53 — Signature changed from (magnet, {x, y}) to (magnet, px, py) to
// eliminate the object literal allocation at every call site. Squared distance
// avoids sqrt. Same pattern as isPlayerNearChest fix.
export function isPlayerNearChickMagnet(
  magnet: ChickMagnet,
  px: number,
  py: number,
): boolean {
  if (magnet.activated) return false;
  const dx = px - magnet.x;
  const dy = py - magnet.y;
  return dx * dx + dy * dy <= CHICK_MAGNET_INTERACT_RADIUS * CHICK_MAGNET_INTERACT_RADIUS;
}

// ─── Rendering ────────────────────────────────────────────────

export const CHICK_MAGNET_SPRITE_URL = '/props/chick_magnet_final.png';

export function renderChickMagnet(
  ctx: CanvasRenderingContext2D,
  magnet: ChickMagnet,
  zoom: number,
  pulse: number,
  pass: 'back' | 'front' | null = null,
  playerY = 0,
  img?: HTMLImageElement | null,
) {
  if (!img) return;

  const cx = magnet.x;
  const cy = magnet.y;

  const feetY = cy + CHICK_MAGNET_FEET_OFFSET;
  if (pass === 'back'  && playerY <= feetY) return;
  if (pass === 'front' && playerY >  feetY) return;

  ctx.save();

  // Ground shadow
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(cx, cy + CHICK_MAGNET_FEET_OFFSET + 10, 60, 18, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  // Glow
  if (magnet.activated) {
    ctx.shadowColor = `rgba(30,130,255,${0.7 + 0.25 * pulse})`;
    ctx.shadowBlur  = (28 + 12 * pulse) * zoom;
  } else {
    ctx.shadowColor = `rgba(220,200,160,${0.35 + 0.15 * pulse})`;
    ctx.shadowBlur  = (12 + 5 * pulse) * zoom;
  }

  // Preserve natural aspect ratio — scale to target height, let width float
  const natW = img.naturalWidth  || CHICK_MAGNET_W;
  const natH = img.naturalHeight || CHICK_MAGNET_H;
  const drawH = CHICK_MAGNET_H;
  const drawW = (natW / natH) * drawH;
  ctx.drawImage(img, cx - drawW / 2, cy - drawH / 2, drawW, drawH);

  ctx.restore();
}
